import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { colors, shadows } from "../styles/GlobalStyles";
import { mediaQueries, rv } from "../styles/responsive";
import {
  Container,
  Section,
  SectionDescription,
  SectionHeader,
  SectionLabel,
  SectionTitle,
} from "./ui";
import { ArrowRightIcon, CodeIcon, LayersIcon } from "./ui/Icons";
import useFetchJson from "../hooks/useFetchJson";
import useLockPageScroll from "../hooks/useLockPageScroll";

type ClientWorkItem = {
  type: string;
  title: string;
  description: string;
};

interface ClientItem {
  name: string;
  industry: string;
  relationship: string;
  homepageUrl?: string;
  summary: string;
  detailDescription: string;
  contributionSummary: string;
  image?: string;
  works: ClientWorkItem[];
  techStack: string[];
}

const ClientsSection = styled(Section)`
  min-height: 100vh;
  justify-content: center;
  overflow: visible;
  padding: ${rv(80, 48)} 0;
  background: linear-gradient(180deg, ${colors.white} 0%, ${colors.slate[50]} 100%);

  @media (max-width: 767px) {
    padding: 48px 0 42px;
  }
`;

const ClientsContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: ${rv(34, 26)};

  @media (max-width: 767px) {
    gap: 20px;
  }
`;

const IntroGrid = styled.div`
  display: grid;
  gap: ${rv(28, 20)};

  ${mediaQueries.desktopUp} {
    grid-template-columns: minmax(320px, 0.9fr) minmax(0, 1.5fr);
    align-items: end;
  }
`;

const ClientsHeader = styled(SectionHeader)`
  text-align: left;
  margin-bottom: 0;

  ${SectionDescription} {
    margin: 0;
  }

  @media (max-width: 767px) {
    ${SectionTitle} {
      margin-bottom: 8px;
    }
  }
`;

const SummaryRail = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: 1px solid ${colors.slate[200]};
  border-radius: ${rv(24, 18)};
  background: rgba(255, 255, 255, 0.78);
  overflow: hidden;
  box-shadow: ${shadows.bento};

  @media (max-width: 767px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const SummaryItem = styled.div`
  min-height: ${rv(112, 82)};
  padding: ${rv(22, 18)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;

  & + & {
    border-left: 1px solid ${colors.slate[200]};
  }

  @media (max-width: 767px) {
    min-height: 72px;
    padding: 14px 10px;

    & + & {
      border-left: 1px solid ${colors.slate[200]};
      border-top: 0;
    }
  }
`;

const SummaryValue = styled.strong`
  color: ${colors.slate[900]};
  font-size: ${rv(28, 22)};
  font-weight: 900;
  line-height: 1;

  @media (max-width: 767px) {
    font-size: 20px;
  }
`;

const SummaryLabel = styled.span`
  color: ${colors.slate[500]};
  font-size: ${rv(13, 12)};
  font-weight: 700;

  @media (max-width: 767px) {
    font-size: 10px;
    line-height: 1.35;
  }
`;

const ClientGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${rv(20, 14)};

  @media (max-width: 899px) {
    grid-template-columns: 1fr;
  }
`;

const ClientCard = styled.article`
  position: relative;
  width: 100%;
  min-height: ${rv(310, 280)};
  padding: ${rv(28, 20)};
  border: 1px solid ${colors.slate[200]};
  border-radius: ${rv(24, 18)};
  background: ${colors.white};
  box-shadow: ${shadows.bento};
  display: flex;
  flex-direction: column;
  gap: ${rv(20, 16)};
  text-align: left;
  overflow: hidden;
  transition: transform 0.24s ease, border-color 0.24s ease,
    box-shadow 0.24s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 5px;
    background: linear-gradient(180deg, #3b82f6, #22c55e);
  }

  &:hover {
    transform: translateY(-4px);
    border-color: ${colors.slate[300]};
    box-shadow: ${shadows.bentoHover};
  }

  @media (max-width: 767px) {
    min-height: 0;
    padding: 18px;
    gap: 14px;
    border-radius: 18px;
  }
`;

const CardActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;

  @media (max-width: 420px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DetailButton = styled.button`
  min-height: 40px;
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 14px;
  border-radius: 14px;
  color: ${colors.white};
  background: ${colors.slate[900]};
  font-size: 13px;
  font-weight: 900;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${colors.slate[800]};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid ${colors.brand[100]};
    outline-offset: 3px;
  }

  @media (max-width: 767px) {
    min-height: 38px;
    font-size: 12px;
  }
`;

const HomepageLink = styled.a`
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  border-radius: 14px;
  color: ${colors.slate[700]};
  background: ${colors.white};
  border: 1px solid ${colors.slate[200]};
  font-size: 13px;
  font-weight: 900;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: ${colors.slate[900]};
    border-color: ${colors.slate[300]};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid ${colors.brand[100]};
    outline-offset: 3px;
  }

  @media (max-width: 767px) {
    min-height: 38px;
    font-size: 12px;
  }
`;

const ClientTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 520px) {
    flex-direction: column;
  }
`;

const ClientIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${rv(16, 12)};
  min-width: 0;
`;

const ClientMark = styled.div`
  width: ${rv(58, 48)};
  height: ${rv(58, 48)};
  flex-shrink: 0;
  border-radius: ${rv(18, 14)};
  display: grid;
  place-items: center;
  color: ${colors.white};
  background: linear-gradient(135deg, ${colors.slate[900]}, ${colors.slate[700]});
  font-size: ${rv(18, 15)};
  font-weight: 900;
  letter-spacing: 0.02em;

  @media (max-width: 767px) {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    font-size: 14px;
  }
`;

const ClientNameGroup = styled.div`
  min-width: 0;
`;

const ClientName = styled.h3`
  color: ${colors.slate[900]};
  font-size: ${rv(24, 20)};
  font-weight: 900;
  line-height: 1.18;
  letter-spacing: -0.035em;

  @media (max-width: 767px) {
    font-size: 18px;
  }
`;

const ClientIndustry = styled.span`
  display: block;
  margin-top: 5px;
  color: ${colors.slate[500]};
  font-size: ${rv(13, 12)};
  font-weight: 700;
`;

const RelationshipBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 6px 11px;
  border-radius: 999px;
  color: ${colors.brand[900]};
  background: ${colors.brand[50]};
  border: 1px solid ${colors.brand[100]};
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
  text-align: right;
  white-space: normal;

  @media (max-width: 520px) {
    text-align: left;
  }
`;

const ClientSummary = styled.p`
  color: ${colors.slate[600]};
  font-size: ${rv(15, 13)};
  line-height: 1.75;

  @media (max-width: 767px) {
    font-size: 12px;
    line-height: 1.6;
  }
`;

const WorkPreview = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
  margin-top: auto;

  @media (max-width: 767px) {
    gap: 8px;
  }
`;

const WorkPreviewItem = styled.li`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  color: ${colors.slate[700]};
  font-size: ${rv(13, 12)};
  font-weight: 800;

  @media (max-width: 767px) {
    font-size: 12px;
    line-height: 1.45;
  }
`;

const WorkDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: ${colors.accent.green};
  box-shadow: 0 0 0 5px rgba(34, 197, 94, 0.12);
`;

const TagRow = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
`;

const ScopeTag = styled.li`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 6px 11px;
  border-radius: 999px;
  color: ${colors.slate[700]};
  background: ${colors.slate[50]};
  border: 1px solid ${colors.slate[200]};
  font-size: 12px;
  font-weight: 800;

  @media (max-width: 767px) {
    min-height: 26px;
    padding: 5px 9px;
    font-size: 11px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);
  overscroll-behavior: contain;

  @media (max-width: 767px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalPanel = styled.article`
  width: min(1040px, 100%);
  max-height: min(88vh, 900px);
  border-radius: 30px;
  background: ${colors.white};
  box-shadow: 0 30px 90px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  display: flex;

  @media (max-width: 767px) {
    width: 100%;
    max-height: 92vh;
    border-radius: 26px 26px 0 0;
  }
`;

const ModalLayout = styled.div`
  width: 100%;
  max-height: inherit;
  min-height: 0;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(280px, 0.85fr) minmax(0, 1.45fr);

  @media (max-width: 899px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }
`;

const ModalAside = styled.aside`
  padding: ${rv(34, 24)};
  color: ${colors.white};
  background:
    linear-gradient(145deg, rgba(15, 23, 42, 0.96), rgba(30, 41, 59, 0.94)),
    linear-gradient(135deg, #3b82f6, #22c55e);
  display: flex;
  flex-direction: column;
  gap: ${rv(24, 18)};

  @media (max-width: 767px) {
    padding: 22px 20px;
    gap: 14px;
  }
`;

const ModalMark = styled.div`
  width: ${rv(68, 56)};
  height: ${rv(68, 56)};
  border-radius: ${rv(20, 16)};
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-size: ${rv(22, 18)};
  font-weight: 900;

  @media (max-width: 767px) {
    width: 48px;
    height: 48px;
    border-radius: 15px;
    font-size: 16px;
  }
`;

const ModalAsideTitle = styled.h3`
  font-size: ${rv(34, 26)};
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.045em;
`;

const ModalAsideText = styled.p`
  color: rgba(255, 255, 255, 0.76);
  font-size: ${rv(15, 13)};
  line-height: 1.75;

  @media (max-width: 767px) {
    display: none;
  }
`;

const ModalMetaList = styled.dl`
  display: grid;
  gap: 14px;
  margin-top: auto;

  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    margin-top: 0;
  }
`;

const ModalMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ModalMetaLabel = styled.dt`
  color: rgba(255, 255, 255, 0.52);
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ModalMetaValue = styled.dd`
  color: ${colors.white};
  font-size: 14px;
  font-weight: 800;
`;

const ModalContent = styled.div`
  padding: ${rv(36, 24)};
  display: flex;
  flex-direction: column;
  gap: ${rv(24, 18)};
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;

  @media (max-width: 767px) {
    padding: 20px 20px 28px;
  }
`;

const ModalTopBar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ModalCloseButton = styled.button`
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border: 1px solid ${colors.slate[200]};
  border-radius: 999px;
  background: ${colors.white};
  color: ${colors.slate[700]};
  font-size: 20px;
  line-height: 1;

  &:hover {
    color: ${colors.slate[900]};
    border-color: ${colors.slate[300]};
  }

  &:focus-visible {
    outline: 3px solid ${colors.brand[100]};
    outline-offset: 2px;
  }
`;

const ModalSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalSectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 9px;
  color: ${colors.slate[900]};
  font-size: ${rv(18, 16)};
  font-weight: 900;
  letter-spacing: -0.02em;

  svg {
    color: ${colors.accent.blue};
  }
`;

const ModalDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${colors.slate[600]};
  font-size: ${rv(15, 14)};
  line-height: 1.8;
`;

const WorkList = styled.ul`
  display: grid;
  gap: 12px;
  list-style: none;
`;

const WorkItem = styled.li`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 14px;
  padding: ${rv(16, 14)} 0;
  border-top: 1px solid ${colors.slate[100]};

  &:last-child {
    border-bottom: 1px solid ${colors.slate[100]};
  }
`;

const WorkIndex = styled.span`
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: ${colors.brand[900]};
  background: ${colors.slate[50]};
  border: 1px solid ${colors.slate[200]};
  font-size: 12px;
  font-weight: 900;
`;

const WorkContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const WorkType = styled.span`
  color: ${colors.accent.blue};
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const WorkTitle = styled.strong`
  color: ${colors.slate[900]};
  font-size: ${rv(16, 14)};
  font-weight: 900;
  line-height: 1.35;
`;

const WorkDescription = styled.p`
  color: ${colors.slate[600]};
  font-size: ${rv(14, 13)};
  line-height: 1.75;
`;

const EmptyState = styled.div`
  width: min(560px, 100%);
  margin: 0 auto;
  padding: ${rv(34, 24)};
  border: 1px solid ${colors.slate[200]};
  border-radius: ${rv(24, 18)};
  background: ${colors.white};
  color: ${colors.slate[500]};
  text-align: center;
  box-shadow: ${shadows.bento};
`;

const getInitials = (name: string) => {
  const compact = name.replace(/\s+/g, "");
  return compact.slice(0, 2).toUpperCase();
};

const Clients: React.FC = () => {
  const {
    data: clientData,
    loading,
    error,
  } = useFetchJson<ClientItem[]>("clients.json");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const selectedClient = useMemo(() => {
    if (!clientData || selectedIndex === null) return null;
    return clientData[selectedIndex] ?? null;
  }, [clientData, selectedIndex]);

  useLockPageScroll(Boolean(selectedClient));

  if (loading) return null;
  if (error) return null;

  const clients = clientData ?? [];
  const totalWorks = clients.reduce((sum, client) => sum + client.works.length, 0);
  const totalTech = new Set(clients.flatMap((client) => client.techStack)).size;

  return (
    <ClientsSection>
      <ClientsContainer>
        <IntroGrid>
          <ClientsHeader>
            <SectionLabel>Client Work</SectionLabel>
            <SectionTitle>Collaboration Records</SectionTitle>
            <SectionDescription>
              고객사별로 수행한 신규 개발, 운영 개선, 시스템 연동 범위를
              분리해 정리했습니다.
            </SectionDescription>
          </ClientsHeader>

          <SummaryRail aria-label="고객사 협업 요약">
            <SummaryItem>
              <SummaryValue>{clients.length}</SummaryValue>
              <SummaryLabel>active client records</SummaryLabel>
            </SummaryItem>
            <SummaryItem>
              <SummaryValue>{totalWorks}</SummaryValue>
              <SummaryLabel>documented work scopes</SummaryLabel>
            </SummaryItem>
            <SummaryItem>
              <SummaryValue>{totalTech}</SummaryValue>
              <SummaryLabel>applied technologies</SummaryLabel>
            </SummaryItem>
          </SummaryRail>
        </IntroGrid>

        {clients.length > 0 ? (
          <ClientGrid>
            {clients.map((client, index) => (
              <ClientCard key={client.name}>
                <ClientTop>
                  <ClientIdentity>
                    <ClientMark>{getInitials(client.name)}</ClientMark>
                    <ClientNameGroup>
                      <ClientName>{client.name}</ClientName>
                      <ClientIndustry>{client.industry}</ClientIndustry>
                    </ClientNameGroup>
                  </ClientIdentity>
                  <RelationshipBadge>{client.relationship}</RelationshipBadge>
                </ClientTop>

                <ClientSummary>{client.summary}</ClientSummary>

                <WorkPreview>
                  {client.works.slice(0, 3).map((work) => (
                    <WorkPreviewItem
                      key={`${client.name}-${work.type}-${work.title}`}
                    >
                      <WorkDot />
                      <span>
                        {work.type} · {work.title}
                      </span>
                    </WorkPreviewItem>
                  ))}
                </WorkPreview>

                <TagRow>
                  {client.techStack.slice(0, 4).map((tech) => (
                    <ScopeTag key={`${client.name}-${tech}`}>{tech}</ScopeTag>
                  ))}
                </TagRow>

                <CardActionRow>
                  <DetailButton
                    type="button"
                    onClick={() => setSelectedIndex(index)}
                  >
                    <span>협업 상세 보기</span>
                    <ArrowRightIcon />
                  </DetailButton>
                  {client.homepageUrl && (
                    <HomepageLink
                      href={client.homepageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${client.name} 홈페이지 새 창으로 열기`}
                    >
                      홈페이지
                    </HomepageLink>
                  )}
                </CardActionRow>
              </ClientCard>
            ))}
          </ClientGrid>
        ) : (
          <EmptyState>등록된 고객사 데이터가 없습니다.</EmptyState>
        )}
      </ClientsContainer>

      {selectedClient && (
        <ModalOverlay
          role="dialog"
          aria-modal="true"
          aria-label={`${selectedClient.name} 고객사 상세 정보`}
          onClick={() => setSelectedIndex(null)}
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
        >
          <ModalPanel
            data-lenis-prevent
            onClick={(event) => event.stopPropagation()}
          >
            <ModalLayout>
              <ModalAside>
                <ModalMark>{getInitials(selectedClient.name)}</ModalMark>
                <div>
                  <ModalAsideTitle>{selectedClient.name}</ModalAsideTitle>
                  <ModalAsideText>{selectedClient.summary}</ModalAsideText>
                </div>
                <ModalMetaList>
                  <ModalMetaItem>
                    <ModalMetaLabel>Industry</ModalMetaLabel>
                    <ModalMetaValue>{selectedClient.industry}</ModalMetaValue>
                  </ModalMetaItem>
                  <ModalMetaItem>
                    <ModalMetaLabel>Relationship</ModalMetaLabel>
                    <ModalMetaValue>{selectedClient.relationship}</ModalMetaValue>
                  </ModalMetaItem>
                </ModalMetaList>
              </ModalAside>

              <ModalContent>
                <ModalTopBar>
                  <ModalCloseButton
                    type="button"
                    aria-label="고객사 상세 팝업 닫기"
                    onClick={() => setSelectedIndex(null)}
                  >
                    ×
                  </ModalCloseButton>
                </ModalTopBar>

                <ModalSection>
                  <ModalSectionTitle>
                    <LayersIcon size={18} />
                    Contribution
                  </ModalSectionTitle>
                  <ModalDescription>
                    <p>{selectedClient.detailDescription}</p>
                    <p>{selectedClient.contributionSummary}</p>
                  </ModalDescription>
                </ModalSection>

                <ModalSection>
                  <ModalSectionTitle>
                    <CodeIcon size={18} />
                    Work Scope
                  </ModalSectionTitle>
                  <WorkList>
                    {selectedClient.works.map((work, index) => (
                      <WorkItem
                        key={`${selectedClient.name}-${work.type}-${work.title}`}
                      >
                        <WorkIndex>{String(index + 1).padStart(2, "0")}</WorkIndex>
                        <WorkContent>
                          <WorkType>{work.type}</WorkType>
                          <WorkTitle>{work.title}</WorkTitle>
                          <WorkDescription>{work.description}</WorkDescription>
                        </WorkContent>
                      </WorkItem>
                    ))}
                  </WorkList>
                </ModalSection>

                <ModalSection>
                  <ModalSectionTitle>Tech & Stack</ModalSectionTitle>
                  <TagRow>
                    {selectedClient.techStack.map((tech) => (
                      <ScopeTag key={`${selectedClient.name}-${tech}`}>
                        {tech}
                      </ScopeTag>
                    ))}
                  </TagRow>
                </ModalSection>
              </ModalContent>
            </ModalLayout>
          </ModalPanel>
        </ModalOverlay>
      )}
    </ClientsSection>
  );
};

export default Clients;
